// src/components/AdminPanel/ReservationTable.js
import React, { useState, useEffect } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { db } from "../../firebase";
import {
  doc,
  deleteDoc,
  updateDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { toast } from "react-toastify";

const ReservationTable = ({ activeCategory }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({
    visible: false,
    application: null,
  });
  const [viewResume, setViewResume] = useState({
    visible: false,
    application: null,
  });
  const [statusPopup, setStatusPopup] = useState({
    visible: false,
    application: null,
  });

  // Get file type for preview
  const getFileType = (url) => {
    if (!url) return "";
    const extension = url.split(".").pop().toLowerCase();
    return extension;
  };

  // Fetch opportunity applications
  useEffect(() => {
    const applicationsQuery = query(
      collection(db, "opportunityApplications"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      applicationsQuery,
      (snapshot) => {
        const applicationsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setApplications(applicationsData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching applications:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter applications by activeCategory
  const currentApplications = applications.filter((app) => {
    if (activeCategory === "upcoming") {
      return !app.status || app.status === "Pending";
    } else {
      return app.status === "Selected" || app.status === "Rejected";
    }
  });

  // Open status update popup
  const handleStatusUpdatePopup = (application) => {
    setStatusPopup({ visible: true, application });
  };

  // Update status
  const handleStatusUpdate = async (status) => {
    if (!statusPopup.application) return;

    setStatusPopup({ visible: false, application: null });

    try {
      const { id } = statusPopup.application;
      const applicationRef = doc(db, "opportunityApplications", id);
      await updateDoc(applicationRef, { status });
      toast.success(`Application marked as ${status}`);
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Failed to update application status.");
    }
  };

  // Open delete confirmation popup
  const handleDeletePopup = (application) => {
    setConfirmDelete({ visible: true, application });
  };

  // Delete application
  const confirmDeleteApplication = async () => {
    const { application } = confirmDelete;
    if (!application) return;

    try {
      const applicationRef = doc(db, "opportunityApplications", application.id);
      await deleteDoc(applicationRef);
      setConfirmDelete({ visible: false, application: null });
      toast.success("Application deleted successfully!");
    } catch (err) {
      console.error("Error deleting application:", err);
      toast.error("Failed to delete application.");
    }
  };

  const handleDeleteClick = () => {
    setConfirmDelete({
      visible: false,
      application: confirmDelete.application,
    });
    confirmDeleteApplication();
  };

  // View Resume Popup
  const handleViewResume = (application) => {
    if (!application.resumeURL) {
      toast.error("No resume found for this candidate!");
      return;
    }
    setViewResume({ visible: true, application });
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div className="candidate-table-container" style={{ marginTop: "20px" }}>
      <div style={{ overflowX: "auto" }}>
        <table border="1" cellPadding="10" style={{ width: "100%" }}>
          <thead style={{ backgroundColor: "#f2f2f2" }}>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>CNIC</th>
              <th>Phone</th>
              <th>Selected Field</th>
              <th>Opportunity Type</th>
              <th>Gender</th>
              <th>Location</th>
              <th>Status</th>
              <th>Applied On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentApplications.length > 0 ? (
              currentApplications.map((application) => {
                const appliedOn = application?.createdAt?.toDate
                  ? application.createdAt.toDate().toLocaleString()
                  : "N/A";

                return (
                  <tr key={application.id}>
                    <td>{application.name || "No Name"}</td>
                    <td>{application.email || "No Email"}</td>
                    <td>{application.cnic || "No CNIC"}</td>
                    <td>{application.phone || "No Phone"}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor: "#FF8000",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          display: "inline-block",
                        }}
                      >
                        {application.selectedField || "N/A"}
                      </span>
                    </td>
                    <td>{application.opportunityTitle || "N/A"}</td>
                    <td>{application.candidateGender || "N/A"}</td>
                    <td>{application.location || "N/A"}</td>
                    <td>
                      <p
                        className="status_chip"
                        style={{
                          backgroundColor:
                            application.status === "Selected"
                              ? "#21b11d"
                              : application.status === "Rejected"
                              ? "#f56565"
                              : "rgb(241, 172, 12)",
                          color: "white",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          display: "inline-block",
                          margin: 0,
                        }}
                      >
                        {application.status || "Pending"}
                      </p>
                    </td>
                    <td>{appliedOn}</td>
                    <td className="icons-flex">
                      {activeCategory === "upcoming" && (
                        <button
                          className="action-button done-button"
                          onClick={() => handleStatusUpdatePopup(application)}
                          title="Mark as done"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                          >
                            <path
                              fill="currentColor"
                              d="m19.77 4.93l1.4 1.4L8.43 19.07l-5.6-5.6l1.4-1.4l4.2 4.2zm0-2.83L8.43 13.44l-4.2-4.2L0 13.47l8.43 8.43L24 6.33z"
                            />
                          </svg>
                        </button>
                      )}

                      <button
                        className="action-button view-button"
                        onClick={() => handleViewResume(application)}
                        title="View Resume"
                      >
                        <i className="fas fa-eye"></i>
                      </button>

                      <button
                        className="action-button delete-button"
                        onClick={() => handleDeletePopup(application)}
                        title="Delete application"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" style={{ textAlign: "center" }}>
                  No applications found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Status Update Popup */}
      {statusPopup.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "32px",
              borderRadius: "16px",
              textAlign: "center",
              width: "340px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span
                style={{ color: "white", fontSize: "18px", fontWeight: "bold" }}
              >
                ✓
              </span>
            </div>

            <h3
              style={{
                margin: "0 0 8px 0",
                color: "#2D3748",
                fontSize: "18px",
              }}
            >
              Update Status
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#718096",
                fontSize: "14px",
              }}
            >
              <strong style={{ color: "#2D3748" }}>
                {statusPopup.application?.name}
              </strong>
              <br />
              <span style={{ fontSize: "12px" }}>
                {statusPopup.application?.selectedField}
              </span>
            </p>

            <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
              <button
                style={{
                  background:
                    "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
                  color: "white",
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "600",
                  flex: 1,
                }}
                onClick={() => handleStatusUpdate("Selected")}
              >
                Selected
              </button>
              <button
                style={{
                  background:
                    "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
                  color: "white",
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "600",
                  flex: 1,
                }}
                onClick={() => handleStatusUpdate("Rejected")}
              >
                Rejected
              </button>
            </div>

            <button
              onClick={() =>
                setStatusPopup({ visible: false, application: null })
              }
              style={{
                backgroundColor: "#6B6FD5",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {confirmDelete.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              padding: "32px",
              borderRadius: "16px",
              textAlign: "center",
              width: "360px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <span
                style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}
              >
                !
              </span>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                color: "#2D3748",
                fontSize: "18px",
              }}
            >
              Confirm Delete
            </h3>
            <p
              style={{
                margin: "0 0 28px 0",
                color: "#718096",
                fontSize: "14px",
                lineHeight: "1.4",
              }}
            >
              Delete application from{" "}
              <strong style={{ color: "#2D3748" }}>
                {confirmDelete.application?.name}
              </strong>
              ? This action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={{
                  backgroundColor: "transparent",
                  color: "#718096",
                  padding: "12px 24px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  cursor: "pointer",
                  flex: 1,
                }}
                onClick={() =>
                  setConfirmDelete({ visible: false, application: null })
                }
              >
                Cancel
              </button>
              <button
                style={{
                  background:
                    "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
                  color: "white",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "600",
                  flex: 1,
                }}
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Resume Modal - IMPROVED MOBILE RESPONSIVE */}
      {viewResume.visible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "10px",
            boxSizing: "border-box",
          }}
          onClick={() => setViewResume({ visible: false, application: null })}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "14px",
              width: "90vw",
              maxWidth: "1200px",
              height: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              border: "1px solid #e5e7eb",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Responsive */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#fafafa",
                flexShrink: 0,
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  minWidth: "0",
                  flex: "1",
                }}
              >
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "600",
                    fontSize: "13px",
                    color: "#374151",
                    flexShrink: 0,
                  }}
                >
                  {viewResume.application?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ minWidth: "0", flex: "1" }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "600",
                      fontSize: "clamp(12px, 4vw, 14px)",
                      color: "#111827",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {viewResume.application?.name}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "10px",
                      color: "#9ca3af",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {viewResume.application?.selectedField} •{" "}
                    {viewResume.application?.opportunityTitle}
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <a
                  href={viewResume.application?.resumeURL}
                  download
                  style={{
                    color: "#374151",
                    padding: "6px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontSize: "clamp(11px, 3vw, 12px)",
                    fontWeight: "500",
                    background: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>📥</span>{" "}
                  <span style={{ display: "inline" }}>Download</span>
                </a>
                <a
                  href={viewResume.application?.resumeURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#374151",
                    padding: "6px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontSize: "clamp(11px, 3vw, 12px)",
                    fontWeight: "500",
                    background: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>🔗</span>{" "}
                  <span style={{ display: "inline" }}>Open</span>
                </a>
                <button
                  onClick={() =>
                    setViewResume({ visible: false, application: null })
                  }
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "clamp(11px, 3vw, 12px)",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, background: "#f9fafb", overflow: "hidden" }}>
              {getFileType(viewResume.application?.resumeURL) === "pdf" ? (
                <iframe
                  src={viewResume.application?.resumeURL}
                  title="Resume"
                  width="100%"
                  height="100%"
                  style={{ border: "none", display: "block" }}
                />
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    color: "#6b7280",
                    padding: "20px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "#f3f4f6",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                    }}
                  >
                    📄
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "clamp(12px, 4vw, 14px)",
                    }}
                  >
                    Preview not available
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "clamp(11px, 3.5vw, 12px)",
                      color: "#9ca3af",
                    }}
                  >
                    This file type can't be previewed in the browser.
                  </p>
                  <a
                    href={viewResume.application?.resumeURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginTop: "4px",
                      color: "#374151",
                      fontSize: "clamp(12px, 3.5vw, 13px)",
                      fontWeight: "500",
                      padding: "8px 20px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      textDecoration: "none",
                      background: "#fff",
                    }}
                  >
                    Open in new tab ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationTable;
